// client/src/pages/VerifyLocationPage.jsx
// On-demand "source of truth" tool: enter an order number, get the CURRENT
// per-line-item assigned locations straight from Shopify (bypasses the mirror).
// Resolves the confusion when the Monitor's order-level location and the stored
// per-item view disagree after a location flip-flop.
import React, { useState, useCallback } from "react";
import { verifyOrderLocation } from "../services/api";

const card = {
  background: "white",
  borderRadius: 16,
  border: "1px solid #e8ecf0",
  padding: 20,
};

const LocBadge = ({ mismatch, children }) => (
  <span
    style={{
      background: mismatch ? "#fef2f2" : "#f0fdf4",
      color: mismatch ? "#991b1b" : "#166534",
      border: `1px solid ${mismatch ? "#fecaca" : "#bbf7d0"}`,
      borderRadius: 6,
      padding: "3px 9px",
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

const Th = ({ children, align = "left" }) => (
  <th
    style={{
      padding: "10px 14px",
      textAlign: align,
      fontSize: 11,
      fontWeight: 700,
      color: "#94a3b8",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      borderBottom: "2px solid #e8ecf0",
      background: "#f8fafc",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </th>
);

const Td = ({ children, align = "left", mono = false }) => (
  <td
    style={{
      padding: "12px 14px",
      textAlign: align,
      fontSize: 13,
      color: "#1e293b",
      fontFamily: mono ? "monospace" : "inherit",
      verticalAlign: "middle",
    }}
  >
    {children}
  </td>
);

const VerifyLocationPage = () => {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const runVerify = useCallback(async () => {
    const order = input.trim();
    if (!order) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      // apiClient's response interceptor already unwraps to response.data,
      // so this resolves to the JSON body directly (not an axios response).
      const data = await verifyOrderLocation(order);
      setResult(data);
    } catch (err) {
      setError(err?.data?.error || err.message || "Lookup failed");
    } finally {
      setLoading(false);
    }
  }, [input]);

  const onKeyDown = (e) => {
    if (e.key === "Enter") runVerify();
  };

  const mismatchCount = result?.lineItems?.filter((l) => l.mismatch).length || 0;
  const okCount = result?.lineItems?.filter((l) => !l.mismatch).length || 0;

  return (
    <main style={{ flex: 1, padding: "28px 32px", maxWidth: 1100, margin: "0 auto", width: "100%" }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 26, fontWeight: 900, color: "#0f172a", letterSpacing: "-0.03em", margin: "0 0 4px" }}>
          Verify Order Location
        </h1>
        <p style={{ color: "#64748b", fontSize: 14, margin: 0 }}>
          Live lookup from Shopify — shows each line item's current assigned location. Bypasses the local mirror.
        </p>
      </div>

      {/* Search box */}
      <div style={{ ...card, display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Order number (e.g. 029917824 or #029917824)"
          style={{
            flex: 1,
            padding: "11px 14px",
            fontSize: 14,
            border: "1px solid #cbd5e1",
            borderRadius: 10,
            outline: "none",
            fontFamily: "monospace",
          }}
        />
        <button
          onClick={runVerify}
          disabled={loading || !input.trim()}
          style={{
            padding: "11px 22px",
            fontSize: 14,
            fontWeight: 700,
            color: "white",
            background: loading || !input.trim() ? "#94a3b8" : "linear-gradient(135deg,#8b5cf6,#6366f1)",
            border: "none",
            borderRadius: 10,
            cursor: loading || !input.trim() ? "default" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {loading ? "Checking…" : "Verify"}
        </button>
      </div>

      {error && (
        <div style={{ ...card, borderColor: "#fecaca", background: "#fef2f2", color: "#991b1b", marginBottom: 20 }}>
          {error}
        </div>
      )}

      {result && !result.found && (
        <div style={{ ...card, textAlign: "center", color: "#64748b" }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🔍</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
            {result.orderNumber} not found in Shopify
          </div>
          <p style={{ margin: "6px 0 0", fontSize: 13 }}>Check the order number and try again.</p>
        </div>
      )}

      {result && result.found && (
        <>
          {/* Summary */}
          <div style={{ ...card, marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 900, color: "#0f172a", fontFamily: "monospace" }}>
                  {result.orderNumber}
                </div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                  Shopify status: {result.orderLevel.shopifyFulfillmentStatus || "—"}
                  {result.cancelledInShopify && " · cancelled in Shopify"}
                  {" · checked "}
                  {new Date(result.checkedAt).toLocaleTimeString()}
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                {mismatchCount > 0 ? (
                  <LocBadge mismatch>{mismatchCount} item{mismatchCount !== 1 ? "s" : ""} at wrong location</LocBadge>
                ) : (
                  <LocBadge>All items at Ship Cube</LocBadge>
                )}
                {okCount > 0 && mismatchCount > 0 && <LocBadge>{okCount} at Ship Cube</LocBadge>}
              </div>
            </div>
            <div style={{ marginTop: 14, fontSize: 12, color: "#64748b" }}>
              Expected location (Ship Cube 3PL) ID: <code>{result.expectedLocationId}</code>
            </div>
          </div>

          {/* Per-line-item table */}
          <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 20 }}>
            <div style={{ padding: "14px 16px", borderBottom: "1px solid #f1f5f9", fontWeight: 800, color: "#0f172a", fontSize: 14 }}>
              Line items ({result.lineItems.length})
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
                <thead>
                  <tr>
                    <Th>SKU</Th>
                    <Th align="center">Qty</Th>
                    <Th>Assigned Location</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {result.lineItems.map((li, i) => (
                    <tr key={`${li.sku}-${li.fulfillment_order_id}-${i}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <Td mono>{li.sku}</Td>
                      <Td align="center">{li.quantity}</Td>
                      <Td>{li.location_name || li.location_id || "—"}</Td>
                      <Td>
                        <LocBadge mismatch={li.mismatch}>
                          {li.mismatch ? "Wrong location" : "Correct (Ship Cube)"}
                        </LocBadge>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Fulfillment orders */}
          {result.fulfillmentOrders?.length > 0 && (
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "14px 16px", borderBottom: "1px solid #f1f5f9", fontWeight: 800, color: "#0f172a", fontSize: 14 }}>
                Shopify fulfillment orders ({result.fulfillmentOrders.length})
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
                  <thead>
                    <tr>
                      <Th>Fulfillment Order</Th>
                      <Th>Location</Th>
                      <Th align="center">Items</Th>
                      <Th>FO Status</Th>
                      <Th>Location Check</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.fulfillmentOrders.map((fo) => (
                      <tr key={fo.fulfillment_order_id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <Td mono>{fo.fulfillment_order_id}</Td>
                        <Td>{fo.location_name || fo.location_id || "—"}</Td>
                        <Td align="center">{fo.item_count}</Td>
                        <Td>{fo.status}</Td>
                        <Td>
                          <LocBadge mismatch={fo.mismatch}>
                            {fo.mismatch ? "Wrong location" : "Correct"}
                          </LocBadge>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
};

export default VerifyLocationPage;
